/**
 * Authentication-aware retry configuration for TanStack Query
 *
 * This utility provides retry logic that respects authentication state
 * and prevents infinite retries when authentication is blocked.
 */

import { logger } from "@/lib/logger";

export interface AuthAwareRetryOptions {
  maxRetries?: number;
  retryDelay?: (attemptIndex: number) => number;
  skipAuthErrors?: boolean;
}

/**
 * A thrown value is `unknown` - TanStack Query hands `retry` whatever the query
 * function rejected with, which is not necessarily an `Error`. These two guards
 * read the only two fields this policy cares about, narrowing at the boundary
 * instead of widening the parameter (main-03: no loose types, no assertions).
 */
function errorMessageOf(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "message" in error) {
    const { message } = error;
    return typeof message === "string" ? message : undefined;
  }
  return undefined;
}

function errorCodeOf(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "code" in error) {
    const { code } = error;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

/**
 * Default retry configuration that respects authentication
 */
export const defaultAuthAwareRetry = (options: AuthAwareRetryOptions = {}) => {
  const {
    maxRetries = 1,
    retryDelay = (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    skipAuthErrors = true,
  } = options;

  return (failureCount: number, error: unknown) => {
    const errorMessage = errorMessageOf(error);
    const errorCode = errorCodeOf(error);

    logger.info("🔄 TanStack Query Retry Check:", {
      failureCount,
      maxRetries,
      errorMessage,
      errorCode,
      errorType: typeof error,
      skipAuthErrors,
    });

    // Don't retry if the error is authentication-related
    if (
      skipAuthErrors &&
      (errorMessage?.includes("User not authenticated") ||
        errorMessage?.includes("Authentication still loading") ||
        errorCode === "AUTH_REQUIRED" ||
        errorCode === "AUTH_LOADING")
    ) {
      logger.info("🚫 TanStack Query: Skipping retry due to authentication error:", {
        message: errorMessage,
        code: errorCode,
        failureCount,
        willRetry: false,
      });
      return false;
    }

    // For other errors, retry up to maxRetries times
    const willRetry = failureCount < maxRetries;
    logger.info("✅ TanStack Query: Will retry:", {
      failureCount,
      maxRetries,
      willRetry,
      errorMessage,
    });
    return willRetry;
  };
};

/**
 * Retry configuration for queries (default behavior)
 */
export const queryRetry = defaultAuthAwareRetry({ maxRetries: 1 });

/**
 * Retry configuration for mutations (more conservative)
 */
export const mutationRetry = defaultAuthAwareRetry({ maxRetries: 1 });

/**
 * Retry configuration for critical operations (more aggressive)
 */
export const criticalRetry = defaultAuthAwareRetry({ maxRetries: 3 });

/**
 * Retry configuration that never retries (for one-shot operations)
 */
export const noRetry = defaultAuthAwareRetry({ maxRetries: 0 });

/**
 * Retry delay function with exponential backoff
 */
export const exponentialBackoff = (attemptIndex: number) =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

/**
 * Retry delay function with linear backoff
 */
export const linearBackoff = (attemptIndex: number) => Math.min(1000 * (attemptIndex + 1), 10000);
